import { B, InlineCompany, InlineClient, L, N } from "./Parts";
import { Client, Company, Quote, QuoteItem } from "../../types";
import { getAmounts } from "../../utils";
import { intToBrl } from "../../utils";
import numero from "numero-por-extenso";
import moment from "moment";

const ContentRentStart = ({
  company,
  client,
  quote,
  equipmentMap,
}: {
  company: Company;
  client: Client;
  quote: Quote;
  equipmentMap: Record<string, string>;
}) => {
  const total = getAmounts(quote).finalRent - quote.feeMonitoring;
  return (
    <div className="tracking-tight flex flex-col gap-4 py-8 pb-16 text-justify">
      <div className="text-center mb-4">
        <B>Contrato de locação de equipamentos de alarme eletrônico e CFTV</B>
      </div>

      <div>
        <B>Das partes</B>
      </div>

      <div>
        <InlineCompany company={company} /> e <InlineClient client={client} />.
      </div>

      <div>
        Contratam a locação de equipamento abaixo especificado, mediante as
        condições contidas nas cláusulas a seguir:
      </div>

      <div>
        <N i="1" /> O Objeto desta locação são os equipamentos constantes
        abaixo:
      </div>

      <EquipmentTable
        equipments={quote.itemsRented}
        equipmentMap={equipmentMap}
        discount={quote.discountRent}
        total={total}
      />

      <div>
        <N i="2" /> Os equipamentos constantes da cláusula anterior serão
        instalados no endereço locatária.
      </div>

      <div>
        <N i="3" /> A Locatária pagará mensalmente, durante 12 (doze) meses,
        contados a partir da data de instalação do equipamento, um aluguel de{" "}
        <strong>
          {intToBrl(total)} (
          {numero.porExtenso(total / 100, numero.estilo.monetario)})
        </strong>{" "}
        mensais renovando-se automaticamente por prazo indeterminado, salvo
        manifestação expressa da Locatária com antecedência mínima de trinta
        (30) dias do termo final ajustado, mediante notificação por escrito.
      </div>

      <div>
        <strong>Parágrafo Primeiro.</strong> Entende-se por data de instalação
        do equipamento o dia em que o mesmo passar a operar no sistema da
        Locadora, após os testes necessários, e for constatada por ambas as
        partes estar o mesmo em condições satisfatórias. Fica ajustado que o
        primeiro aluguel será faturado no dia da instalação. Os demais aluguéis
        serão sempre devidos para o dia escolhido pela Contratante/Locadora, e
        as demais sucessivamente às vencidas mediante duplicata mercantil
        fornecida pela Locadora à Locatária.
      </div>

      <div>
        <strong>Parágrafo Segundo.</strong> Eventuais atrasos de faturamento por
        parte da Locatária e consequentes postergações das respectivas datas de
        vencimento não serão jamais entendidos, em hipótese alguma, como novação
        contratual e/ou alteração de regra de faturamento acima estabelecida, a
        qual, quando retomada, prevalecerá sempre.
      </div>

      <div>
        <strong>Parágrafo Terceiro.</strong> O valor de locação estipulado neste
        contrato será reajustado com base na variação do IGPM (Índice Geral de
        Preços do Mercado), variação esta, a ser aplicada sempre na menor
        periodicidade admitida em lei, em qualquer época de vigência deste
        contrato. Na hipótese de suspensão, extinção ou vedação do uso do IGPM
        como índice de atualização de preços, fica desde já eleito o índice que
        oficialmente vier a substituí-lo.
      </div>

      <div>
        <N i="4" /> A Locatária fica ciente, ainda, de que eventuais danos
        causados em componentes fornecidos por conta deste instrumento, por
        culpa dela, Locatária, ou de terceiros não autorizados pela Contratada e
        que resultem em dano ao equipamento fornecido neste caso, será dela
        cobrado.
      </div>

      <div>
        <N i="5" /> Tendo em vista que o equipamento é de propriedade da
        Locadora, a qual está obrigada pela manutenção técnica, fica ciente
        desde logo, a Locatária de que, caso diagnósticos sucessivos do técnico
        da Locadora identifiquem os componentes ou materiais, fornecidos por
        terceiros, como causa de eventuais falhas no sistema e/ou danos no
        próprio equipamento, informada a Locatária, por escrito, sobre o fato, a
        Locadora passará a ter direito de cobrar os custos adicionais
        decorrentes dos atendimentos técnicos posteriores e os danos causados a
        seus equipamentos.
      </div>

      <div>
        <N i="6" /> A Locadora oferece plena garantia do perfeito funcionamento
        do equipamento, quando da respectiva instalação, obedecidas as
        especificações técnicas, podendo o equipamento, objeto do presente
        contrato, ser previamente revisado, dentro dos mais rigorosos padrões
        técnicos e de controle de qualidade.
      </div>

      <div>
        <N i="7" /> A Locadora entregará e instalará o equipamento no local
        indicado pela Locatária, constantes na <B>Ficha de Monitoramento</B>, em
        perfeitas condições de uso a que se destina, do que receberá um
        comprovante da Locadora. As despesas de preparação das instalações
        elétricas e civis, entretanto, são de responsabilidade exclusiva da
        Locatária, a qual receberá da Locadora as especificações
        correspondentes.
      </div>

      <div>
        <N i="8" /> É de responsabilidade da Locatária:
      </div>

      <div>
        <L l="a" /> parcial; Usar o equipamento corretamente e não sublocar,
        ceder nem transferir a locação, total ou parcial;
      </div>

      <div>
        <L l="b" /> Manter o equipamento no local exato da instalação. Qualquer
        mudança só será permitida mediante o prévio consentimento por escrito da
        Locadora. Quaisquer despesas decorrentes dessas mudanças de local,
        inclusive, mas não exclusivamente, transporte, montagem, colocação do
        equipamento no novo local indicado e novas instalações elétricas, correm
        por conta exclusiva da Locatária;
      </div>

      <div>
        <L l="c" /> Não introduzir modificações de qualquer natureza no
        equipamento;
      </div>

      <div>
        <L l="d" /> Defender e fazer valer todos os direitos de propriedade e de
        posse da Locadora sobre o equipamento, inclusive impedindo sua penhora,
        sequestro, arresto, arrecadação, etc., por terceiros, notificando-os
        sobre os direitos de propriedade e de posse da Locadora sobre o
        equipamento;
      </div>

      <div>
        <L l="e" /> Comunicar imediatamente à Locadora qualquer intervenção ou
        violação por terceiros de qualquer dos seus direitos em relação ao
        equipamento;
      </div>

      <div>
        <L l="f" /> Permitir o acesso de pessoal autorizado da Locadora para
        realização da manutenção ou reparos do equipamento e, ainda, para o seu
        desligamento ou remoção, nas hipóteses cabíveis;
      </div>

      <div>
        <L l="g" /> Responsabilizar-se por qualquer dano, prejuízo ou
        inutilização do equipamento, ressalvadas as hipóteses de casos fortuitos
        ou de força maior, bem como pelo descumprimento de qualquer de suas
        obrigações previstas neste contrato ou em lei;
      </div>

      <div>
        <L l="h" /> Não permitir que terceiros não autorizados ou não
        credenciados pela Locadora intervenham nas partes e nos componentes
        internos do equipamento.
      </div>

      <div>
        <N i="9" /> A Locatária obriga-se a pagar pontualmente os aluguéis via
        boleto bancário emitido pela
      </div>

      <div>
        <N i="10" /> As faturas não pagas até o vencimento serão acrescidas de
        correção monetária, aplicada pelos dias de atraso, cominada, também,
        multa de dois por cento (2%) e juros de mora de um por cento (1%) ao
        mês, sem prejuízo das demais sanções aplicáveis, dentre as quais o
        desligamento temporário do equipamento, a suspensão da Assistência
        Técnica ou a rescisão deste contrato.
      </div>

      <div>
        <N i="11" /> Sem prejuízo dos acréscimos moratórios estabelecidos no
        item acima, à Locatária, se não cumprir as obrigações deste contrato,
        será cominada a multa equivalente a três (3) vezes o valor do aluguel
        mínimo mensal vigente à época, mais custas, despesas e honorários
        advocatícios, em caso de cobrança judicial, ficando ainda a Locadora com
        o direito de considerar rescindido o presente contrato.
      </div>

      <div>
        <N i="12" /> A recusa da devolução do equipamento locado obriga a
        Locatária a pagar pelo período em que o equipamento deixar de ser
        utilizado pela Locadora em novas locações. Em caso de dano, que
        inviabilize a funcionalidade do equipamento locado, obriga a Locatária
        ao ressarcimento do valor do equipamento mediante apresentação de nota
        fiscal de compra do novo equipamento igual ou similar ao avariado.
      </div>

      <div>
        <N i="13" /> As partes ajustam que, na infração de qualquer das
        cláusulas contratuais por parte da Locatária, a Locadora poderá, além de
        rescindir este contrato, como previsto acima, exigir e obter imediata
        devolução do equipamento, cabendo-lhe inclusive, na via judicial.
      </div>

      <div>
        <N i="14" /> Poderá ainda a Locadora, facultativamente, considerar
        rescindida a locação e retirar o equipamento locado nas hipóteses de
        falência ou insolvência da Locatária.
      </div>

      <div>
        <N i="15" /> A infração, por qualquer das partes, das obrigações
        assumidas no presente contrato dará à outra o direito de rescindi-lo,
        independentemente de intimação judicial ou extrajudicial, bastando, para
        isso, aviso por escrito, com prazo de trinta (30) dias contados da
        inadimplência.
      </div>

      <div>
        <N i="16" /> O cancelamento deste contrato antes do prazo de 12 (doze)
        meses acarretará multa de 30% (trinta por cento) do valor das
        mensalidades a vencer.
      </div>

      <div>
        <N i="17" />
        <B>
          Fará parte integrante deste instrumento de contrato de locação, o
          contrato de prestação de serviços de monitoração e manutenção em
          alarmes eletrônicos monitorados e a ficha de monitoramento,
          reconhecendo a Locatária todas as cláusulas previstas nesse
          instrumento.
        </B>
      </div>

      <div>
        <N i="18" /> Fica eleito o Foro da cidade de Vitória/ES, para dirimir
        quaisquer questões oriundas deste contrato, renunciando as partes a
        qualquer outro, por mais privilegiado que seja.
      </div>

      <div className="flex flex-col gap-4 break-inside-avoid">
        <div>
          <N i="19" /> E por estarem de pleno e comum acordo com todas as
          cláusulas, firmam o presente instrumento, por si e eventuais
          sucessores, em duas (2) vias de igual teor, para um só efeito, com
          vigência a partir da data de sua assinatura, na presença de duas
          testemunhas que a tudo assistiram.
        </div>

        <div className="text-center my-4">
          Vitória/ES, {moment.utc().format("DD [de] MMMM [de] YYYY")}
        </div>

        <div className="text-center mt-16">
          <B>{company.name}</B>
          <div>(Contratada)</div>
        </div>

        <div className="text-center mt-16">
          <B>{client.name}</B>
          <div>(Contratante)</div>
        </div>
      </div>
    </div>
  );
};

export default ContentRentStart;

const EquipmentTable = ({
  equipments,
  equipmentMap,
  discount,
  total,
}: {
  equipments: QuoteItem[];
  equipmentMap: Record<string, string>;
  discount: number;
  total: number;
}) => {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="uppercase text-white font-semibold bg-sky-900">
          <th className="p-2 text-left">QTD</th>
          <th className="p-2 text-left">Descrição</th>
          <th className="p-2 text-right">V. Unit</th>
          <th className="p-2 text-right">V. Total</th>
        </tr>
      </thead>

      <tbody>
        {equipments.map((equipment, i) => (
          <tr key={i} className="border-b border-gray-500">
            <td className="p-2 border-l border-gray-500">{equipment.amount}</td>
            <td className="p-2 border-l border-gray-500">
              {equipmentMap[equipment.equipmentUuid]}
            </td>
            <td className="p-2 border-l border-gray-500 whitespace-nowrap text-right">
              {intToBrl(equipment.price)}
            </td>
            <td className="p-2 border-x border-gray-500 whitespace-nowrap text-right">
              {intToBrl(equipment.price * equipment.amount)}
            </td>
          </tr>
        ))}

        {discount > 0 && (
          <tr className="border-b border-gray-500">
            <td className="p-2 border-l border-gray-500" />
            <td colSpan={2} className="p-2 border-l border-gray-500">
              Desconto
            </td>
            <td className="p-2 border-x border-gray-500 whitespace-nowrap text-right">
              – {intToBrl(discount)}
            </td>
          </tr>
        )}

        <tr className="border-b border-gray-500">
          <td className="p-2 border-l border-gray-500" />
          <td colSpan={2} className="p-2 border-l border-gray-500 font-bold">
            Total
          </td>
          <td className="p-2 border-x border-gray-500 whitespace-nowrap font-bold text-right">
            {intToBrl(total)}
          </td>
        </tr>
      </tbody>
    </table>
  );
};
